/**
 * Delivery-status presentation map (Story 7.9).
 *
 * Maps the 6 delivery-lifecycle statuses (db SCHEDULED_REPORT_LOG_STATUS_VALUES,
 * mirrored as DeliveryStatus in schedulesApi.ts) to a human label + a Tailwind
 * badge class. failed/bounced are the error states (red) — the history table
 * visually highlights their rows and offers a Retry action.
 */

import type { DeliveryStatus } from "../../lib/schedulesApi";

export interface StatusPresentation {
  label: string;
  /** Tailwind classes for the status pill (bg + text). */
  badgeClass: string;
  /** True for failed/bounced — the row is highlighted + Retry is offered. */
  isError: boolean;
}

export const DELIVERY_STATUS_META: Record<DeliveryStatus, StatusPresentation> = {
  pending: {
    label: "Pending",
    badgeClass: "bg-muted text-muted-foreground",
    isError: false,
  },
  generating: {
    label: "Generating",
    badgeClass: "bg-blue-500/10 text-blue-700",
    isError: false,
  },
  sending: {
    label: "Sending",
    badgeClass: "bg-amber-500/10 text-amber-700",
    isError: false,
  },
  delivered: {
    label: "Delivered",
    badgeClass: "bg-green-500/10 text-green-700",
    isError: false,
  },
  failed: {
    label: "Failed",
    badgeClass: "bg-red-500/10 text-red-700",
    isError: true,
  },
  bounced: {
    label: "Bounced",
    badgeClass: "bg-red-500/10 text-red-700",
    isError: true,
  },
};

/** Resolve a status to its presentation, tolerating an unknown value defensively. */
export function statusMeta(status: string): StatusPresentation {
  return (
    DELIVERY_STATUS_META[status as DeliveryStatus] ?? {
      label: status,
      badgeClass: "bg-muted text-muted-foreground",
      isError: false,
    }
  );
}
